/*
 Productivity Landing Page Component:
 1. Manages welding productivity measurements and consumable tracking
 2. Allows entry of weld consumable data (batch numbers, weights, quantities)
 3. Calculates weld metal quantities based on correction factors
 4. Generates productivity reports for welding operations

 Backend Services Used:
 - getAllOnlySlipNos: Gets all WI slip numbers from PROD_REPORT table for dropdown selection
 - getWIDataHeader: Fetches WI header data by slip number from WI_HEADER table for new productivity entries
 - getProdMeasure: Gets existing productivity data by slip number from PROD_REPORT table
 - addNewProductivity: Saves/updates productivity measurements to PROD_REPORT table

 Database Tables:
 - WI_HEADER: Source table for welding instruction data (PK: "WI_HEADER", SK: slip_number)
 - PROD_REPORT: Productivity measurements table (PK: "PROD_REPORT", SK: slip_number#SK#index)

 Key Features:
 - Editable grid for consumable data entry (batch numbers, weights, quantities)
 - Item consumable is entered in WPS, while selecting Item for a process, the same is used here
 - Automatic calculation of weld metal quantities using correction factors
 - Validation for numeric fields and alphanumeric batch numbers
 - Support for both Kgs and Nos (pieces) measurements
 - Real-time grid updates with cell editing capabilities
*/
import React, {
  useState,
  useRef,
  forwardRef,
  useImperativeHandle,
  useEffect
} from "react";
import SuccessAlertModal from "../../Components/Modals/successAlertModal";
import ConfirmAlertModal from "../../Components/Modals/confirmAlertModal";
import { Row, Col, Container, Form, Button } from "react-bootstrap";
import GlobalColumnDefinations, { getHeader_FieldName } from "../utility/grid/definitions/AgGridDefination";
import GridMaster from "../utility/grid/GridMaster";
import Toaster, { showSuccess as showSuccessToast, showWarning, showError } from "../utility/ui/notifications/Toaster";
import { SearchSelect } from "../utility/forms/inputs/search-select";
import { getAllOnlySlipNos, getWIDataHeader, addNewProductivity, getProdMeasure } from "../../Services/WI-services";
import { numberMax6Digits, isAlphanumericWithDashDot } from "../../functions/validations";
import { errorstyle, normalstyle } from "../../functions/welding-functions";

const PRODLandPage = forwardRef((props, ref) => {
  const [filteredResult, setFilteredResult] = useState([]);
  const [columnDefs, setcolumnDefs] = useState([]);
  const [, setShowFormadd] = useState(false);
  const [reload, setReload] = useState(false);
  const [, setExcelData] = useState([]);
  const [ProjectDetails, setProjectDetails] = useState([]);
  const [wiDetails, setWIDetails] = useState([]);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [displayMsg, setDisplayMsg] = useState(false);
  const [error] = useState({
    SK: {},
    PK: {},
    wld_status: {},
    wld_code: {},
    wld_p_category: {},
    wld_description: {},
    wld_createdby: {},
    wld_createdon: {},
    wld_updatedby: {},
    wld_updatedon: {},
  });

  const [WiObj, setWiObj] = useState({
    Processes: [
      {
        SK: "",
        WPSD_WP_FillerMetalProductForm: "",
        WPSH_consumRadio: "",
        WPSD_WP_ArcTransferMode: "",
        WPSD_WP_CurrentPolarity: "",
        WPSD_WP_Multiple_SinglePassPerSide: "",
      },
    ],
    PK: "WI_HEADER",
    SK: "",
    WI_Slip: "",
    WI_Project_No: "",
    WI_Joint_No: "",
    WI_JointDesign: "",
    WI_WPS_No: "",
    WI_Process: "",
    WI_Type: "",
    WI_Overlay_Joint_BMT: "",
    WI_PipePlate: "",
    WI_Pipe_Diameter: "",
    WI_Plate_No: "",
    WI_Welding_Position: "",
    WI_Welder_Id: "",
    WI_Welder_Name: "",
    wld_WPSType: "",
    WPSH_TRQ_BaseMetalCRO_Max: "",
    WPSH_BM_WeldingTo_PNo: "",
    WI_24hrs: "",
    WI_Slip_Status: "",
    WI_print: "",
    WCS_Project_No: "",
    WI_plant: "",
  });


  const childRef = useRef();


  /**
   * PRODUCTION GRID COLUMN DEFINITIONS WITH RENDERERS
   * 
   * RENDERER TYPES EXPLAINED:
   * - PRODSelect: Dropdown renderer for "Kgs" or "Nos" selection
   * - ItemGroup: Dropdown renderer for material group selection with correction factors
   * - dependentField: Conditional editing based on unit type selection
   * - Number: Numeric input with validation
   * 
   * CONSUMABLE FIELD INHERITANCE:
   * - WI_FM_Size, WI_ItemCode, WI_ItemDesc: Read-only (from WPS via WI)
   * - WI_WCinKgElectrode: Read-only weight per electrode (from WPS)
   * - Production fields: Editable for actual consumption tracking
   */
  const tmpcolumnDefs = [
    {
      headerName: "Filler Metal Size",
      field: "WI_FM_Size",
      type: "Text",
      isCheckbox: false,
      // READ-ONLY: Inherited from WPS via WI
    },
    {
      headerName: "Item Code",
      field: "WI_ItemCode",
      type: "Text",
      isCheckbox: false,
      // READ-ONLY: Item code from WPS WPSD_WP_WeldConsumable[] via WI
    },
    {
      headerName: "Item Description",
      field: "WI_ItemDesc",
      type: "Text",
      isCheckbox: false,
      // READ-ONLY: Item description from WCS (Welding Consumable Specification)
    },
    {
      headerName: "Batch Number",
      field: "WI_WP_BatchNumber",
      type: "Text",
      isCheckbox: false,
      editable: true,
      // EDITABLE: For traceability, validates alphanumeric with dash/dot
    },
    {
      headerName: "WC in Kgs/Nos",
      field: "WI_WCinKgsNos",
      type: "PRODSelect",  // RENDERER: Dropdown with "Kgs" and "Nos" options
      isCheckbox: false,
      editable: true,
      cellStyle: { borderRadius: "4px" },
      cellClass: "hover-border"
      // CONTROLS: Which quantity field is enabled (Kgs or Nos)
    },
    {
      headerName: "WC in Kgs",
      field: "WI_WCinKgs",
      type: "Number",
      isCheckbox: false,
      editable: "dependentField",  // CONDITIONAL: Enabled when "Kgs" selected
      groupDefaultExpanded: 1,
      suppressAggFuncInHeader: true,
      enableCellChangeFlash: true,  // VISUAL: Highlights cell changes
      cellStyle: { borderRadius: "4px" },
      cellClass: "hover-border"
      // VALIDATION: Must be positive number, max 6 digits
    },
    {
      headerName: "WC in Nos",
      field: "WI_WCinNos",
      type: "Number",
      isCheckbox: false,
      editable: "dependentField",  // CONDITIONAL: Enabled when "Nos" selected
      cellStyle: { borderRadius: "4px" },
      cellClass: "hover-border"
      // TRIGGERS: Auto-calculation of WI_WCinKgs when changed
      // Formula: WI_WCinKgs = WI_WCinNos × WI_WCinKgElectrode
    },
    {
      headerName: "In Kg/Electrode",
      field: "WI_WCinKgElectrode",
      type: "Number",
      isCheckbox: false,
      // READ-ONLY: Calculated from WPS parameters, used in Nos→Kgs conversion
    },
    {
      headerName: "Item Group",
      field: "WI_ItemGroup",
      isCheckbox: false,
      type: "ItemGroup",  // RENDERER: Dropdown with material groups
      editable: true,
      cellStyle: { borderRadius: "4px" },
      cellClass: "hover-border"
      // DATA SOURCE: ItemGroup master table/dropdown PMItemGroup in utility
      // FUNCTIONALITY: Auto-populates correction factor when selected
      // FORMAT: "GroupCode-Description" (splits on "-" for processing)
      // CONTAINS: Material classification codes with associated correction factors
      
    },
    {
      headerName: "Correction Factor",
      field: "WI_CorrectionFactor",
      type: "Text",
      isCheckbox: false,
      editable: false,  // AUTO-POPULATED: From ItemGroup master data selection
      cellStyle: { borderRadius: "4px" },
      cellClass: "hover-border"
      // DATA SOURCE: ItemGroup master table - each group has associated correction factor
      // PURPOSE: Accounts for welding efficiency, spatter loss, stub waste
      // TYPICAL RANGE: 1.1 to 1.5 depending on welding process
    },
    {
      headerName: "WM in Kgs",
      field: "WI_WMinKgs",
      type: "Number",
      isCheckbox: false,
      cellRenderer: "",  // CALCULATED FIELD: No special renderer needed
      valueSetter: true,
      valueGetter: true,
      editable: false,  // READ-ONLY: Calculated value
      // FORMULA: WI_WMinKgs = WI_WCinKgs ÷ CorrectionFactor
      // PURPOSE: Net consumable weight for accurate costing
    },
  ];

  const fetchSlipNos = async () => {
    let result = await getAllOnlySlipNos();

    setProjectDetails(result);
  };

  let wiResult;
  let prodResult;
  /**
   * CONSUMABLE DATA RETRIEVAL FUNCTION
   * 
   * STRATEGY:
   * 1. Check existing production data first
   * 2. If none, fetch WI data (contains WPS-derived consumables)
   * 3. Transform WI arrays into individual production records
   */
  const fetchSlipDetails = async (SlipList) => {
    try {
      /**
       * CONSUMABLE DATA RETRIEVAL WITH SOURCES:
       * 
       * 1. Production Data: getProdMeasure() - Existing production records
       * 2. WI Data: getWIDataHeader() - Contains WPS-derived consumable arrays:
       *    - WI_WeldConsumable[]: Item codes from WPS WPSD_WP_WeldConsumable[]
       *    - WI_ItemDesc[]: Descriptions from WCS via WI processing
       *    - WI_FillerMetalSize[]: Sizes from WPS WPSD_WP_FillerMetalSIze[]
       *    - WI_WCinKgElectrode[]: Calculated weights from WPS parameters
       *    - WI_WP_BatchNumber[]: Batch tracking from WI WPSD_WP_BatchNumber[]
       * 
       * MASTER DATA SOURCES (for renderers):
       * - ItemGroup dropdown: ItemGroup master table with correction factors
       * - Correction factors: Auto-populated from ItemGroup selection
       * - PRODSelect options: Hardcoded "Kgs"/"Nos" values
       */
      prodResult = await getProdMeasure({
        WI_Slip: SlipList,
      });

      if (prodResult.length > 0) {
        setDisplayMsg(true);
      } else if (prodResult.length === 0) {
        setDisplayMsg(false);
        wiResult = await getWIDataHeader({
          WI_Slip: SlipList,
        });
      }


      const result = prodResult.length !== 0 ? prodResult : wiResult;

      if (result.length !== 0 && prodResult.length > 0) {
        setFilteredResult(result);
        setWIDetails(result);
      } else if (result.length !== 0 && prodResult.length === 0) {
        await showGridDetails(result);
        setWIDetails(result);
      }

      if (!result || result.length === 0) {
        setWiObj((prev) => ({
          ...prev,
          WI_Slip: "",
          WI_Welder_Id: "",
          WI_Project_No: "",
          WI_WPS_No: "",
          WI_Joint_No: "",
          WI_JointDesign: "",
          WI_Process: "",
          WI_CreatedBy: "",
          WI_Welding_Position: "",
          WI_FM_AWS: "",
          WI_FM_Brand: "",
          WI_Flux: "",
          WI_Flux_Brand: "",
          WI_SlipDate: "",
          WI_reprintDate: "",
          WI_plant: "",
        }));
      }
    } catch (error) {
      console.error("Error fetching data:", error);
    }
  };


  const headerName = (field) => {
    let name = getHeader_FieldName(field, tmpcolumnDefs, "getHeader");
    return name;
  };

  const fieldName = (header) => {
    let name = getHeader_FieldName(header, tmpcolumnDefs, "getField");
    return name;
  };

  /**
   * WI TO PRODUCTION DATA TRANSFORMATION
   * 
   * CONVERTS: WI header with consumable arrays → Individual production records
   * 
   * CONSUMABLE DATA INHERITANCE (WPS → WI → Production):
   * - WI_FillerMetalSize[i] → WI_FM_Size (from WPS)
   * - WI_WeldConsumable[i] → WI_ItemCode (from WPS) 
   * - WI_WCinKgElectrode[i] → weight per electrode (from WPS)
   * - WI_WP_BatchNumber[i] → batch tracking (from WI)
   */
  let tempArray = [];
  const showGridDetails = async (wiDetails) => {


    let fillerlength = wiDetails[0]?.WI_FillerMetalSize.filter(item => item !== "")
    try {
      if (wiDetails) {
        for (let i = 0; i < fillerlength?.length; i++) {
          let gridData = {
            PK: "PROD_REPORT",
            SK: wiDetails[0].WI_Slip + "#SK#" + i,
            WI_Slip: wiDetails[0].WI_Slip,
            WI_Welder_Name: wiDetails[0].WI_Welder_Name,
            WI_SlipDate: wiDetails[0].WI_SlipDate,
            WI_reprintDate: wiDetails[0].WI_reprintDate,
            WI_plant: wiDetails[0].WI_plant,
            WI_Slip_Status: wiDetails[0].WI_Slip_Status,
            WI_Type: wiDetails[0].WI_Type,
            // CONSUMABLE DATA FROM WPS VIA WI:
            WI_FM_Size: wiDetails[0]?.WI_FillerMetalSize[i],        // SOURCE: WPS WPSD_WP_FillerMetalSIze[i]
            WI_ItemCode: wiDetails[0].WI_WeldConsumable[i],         // SOURCE: WPS WPSD_WP_WeldConsumable[i] 
            WI_ItemDesc: wiDetails[0].WI_ItemDesc[i],               // SOURCE: WCS (Welding Consumable Spec) via WI
            WI_WP_BatchNumber: wiDetails[0].WI_WP_BatchNumber[i],   // SOURCE: WI WPSD_WP_BatchNumber[i]
            WI_WCinKgElectrode: wiDetails[0].WI_WCinKgElectrode[i], // SOURCE: WPS weight calculation
            // PRODUCTION FIELDS (User Input/System Generated):
            WI_WCinKgsNos: "",        // USER INPUT: "Kgs" or "Nos" selection via PRODSelect renderer
            WI_WCinKgs: "",           // USER INPUT: Direct weight OR calculated from Nos × KgElectrode
            WI_WCinNos: "",           // USER INPUT: Number of electrodes/units consumed
            WI_ItemGroup: "",         // USER INPUT: Selected from ItemGroup renderer dropdown
            WI_CorrectionFactor: "",  // AUTO-POPULATED: From ItemGroup selection (master data)
            WI_WMinKgs: "",           // CALCULATED: WI_WCinKgs ÷ CorrectionFactor
            WI_Welder_Id: wiDetails[0].WI_Welder_Id,
            WI_Project_No: wiDetails[0].WI_Project_No,
            WI_WPS_No: wiDetails[0].WI_WPS_No,
            WI_Joint_No: wiDetails[0].WI_Joint_No,
            WI_JointDesign: wiDetails[0].WI_JointDesign,
            WI_Process: wiDetails[0].WI_Process,
            WI_CreatedBy: wiDetails[0].WI_CreatedBy,
            WI_Welding_Position: wiDetails[0].WI_Welding_Position,
            WI_FM_AWS: wiDetails[0].WI_FM_AWS,
            WI_FM_Brand: wiDetails[0].WI_FM_Brand,
            WI_Flux: wiDetails[0].WI_Flux,
            WI_Flux_Brand: wiDetails[0].WI_Flux_Brand,
          };
          tempArray.push(gridData);
        }

        setFilteredResult(tempArray);
      }

    } catch (error) {
      console.error("Error fetching data:", error);
    }
  };



  /**
   * GRID OPTIONS FOR CONSUMABLE TRACKING
   * 
   * singleClickEdit: Enables immediate editing on cell click
   * - Improves user experience for production data entry
   * - Works with renderers to provide dropdown/input functionality
   * - Triggers CellEditing function for automatic calculations
   */
  const gridOptions = {
    singleClickEdit: true,
  };
  /**
   * CONSUMABLE CALCULATION: Automatic Weight Calculation
   * Formula: WI_WCinKgs = WI_WCinNos × WI_WCinKgElectrode
   * WI_WCinKgElectrode comes from WPS via WI (weight per electrode)
   */
  const CellEditing = (e) => {
    if (e.colDef.field == "WI_WCinNos") {
      e.api.getRowNode(e.node.rowIndex).setDataValue('WI_WCinKgs', e.value * e.data.WI_WCinKgElectrode)
    }
  };

  /**
   * COLUMN DEFINITION PROCESSING WITH RENDERERS
   * 
   * PROCESS:
   * 1. GlobalColumnDefinations processes tmpcolumnDefs
   * 2. Applies appropriate renderers based on "type" field:
   *    - "PRODSelect": Creates dropdown for Kgs/Nos selection
   *    - "ItemGroup": Creates dropdown with material groups + correction factors
   *    - "dependentField": Enables/disables based on unit type selection
   *    - "Number": Applies numeric validation and formatting
   * 3. Sets up cell editing behaviors and validation rules
   * 4. Connects to master data sources:
   *    - ItemGroup renderer: Fetches from ItemGroup master table
   *    - Correction Factor: Auto-populated when ItemGroup selected
   *    - Item codes/descriptions: Pre-populated from WPS via WIion rules
   */
  const fetchColumnDefns = async (params) => {
    const result1 = GlobalColumnDefinations(tmpcolumnDefs);
    let result = [];

    result1.forEach((e) => {
      result.push(e);
    });
    setcolumnDefs(result);
  };

  useEffect(() => {
    fetchColumnDefns();
  }, [reload]);

  useEffect(() => {
    fetchSlipNos();
  }, []);

  useImperativeHandle(ref, () => ({
    handleAdd: async () => {
      setShowFormadd(true);
      setReload(true);
    },
  }));



  const [showSuccess, setShowSuccess] = useState(false);
  const msg = "";

  const validate = async (e) => {
    let isError = false;

    if (WiObj.WI_Slip === "") {
      isError = true;
      error.WI_Slip = {
        status: true,
      };

      showWarning("Please Select Mandatory field Slip No");
    }

    return isError;
  };

  const handlesubmition = async (e) => {
    e.preventDefault();

    if (!(await validate())) {
      try {
        let hasValidationErrors = false;
        /**
         * CONSUMABLE DATA PROCESSING:
         * - BatchNo: Traceability from WPS through WI
         * - WCinKgs/WCinNos: Actual consumption quantities
         * - ItemGroup: Material classification for costing
         * - CorrectionFactor: Efficiency factor (accounts for waste/spatter)
         */
        const updatedResult = filteredResult.map((item) => {
          let BatchNo = item.WI_WP_BatchNumber.toString();
          let WCinKgs = parseFloat(item.WI_WCinKgs);
          let WCinNos = parseFloat(item.WI_WCinNos);
          let WCinKgsNos = item.WI_WCinKgsNos;
          let ItemGroup = item?.WI_ItemGroup.split("-")[0];
          let CorrectionFactor = item.WI_CorrectionFactor;




          if (BatchNo !== "") {

            if (isAlphanumericWithDashDot(BatchNo).status === true) {
              hasValidationErrors = true;
              showError(
                "The BatchNo field allowed with alpahanumeric data with dot and dash only  " +
                item.WI_FM_Size
              );
              return item;
            }
          }

          /**
           * CONSUMABLE VALIDATION:
           * - ItemGroup: Required for material costing and inventory
           * - CorrectionFactor: Required for accurate net weight calculation
           */
          if (ItemGroup === "" || CorrectionFactor === "") {


            hasValidationErrors = true;
            showError("Please Select Item Group for " + item.WI_FM_Size);
            return item;
          }

          if (WCinKgsNos === "") {
            hasValidationErrors = true;
            showError(
              "Please Select WCinKgsNos as Kgs or Nos: " + item.WI_FM_Size
            );
            return item;
          }
          else if (WCinKgsNos !== "") {
            if (WCinKgsNos === "Kgs") {


              if (
                isNaN(WCinKgs) ||
                WCinKgs < 0 ||
                WCinKgs === 0 ||
                WCinKgs === undefined
              ) {
                hasValidationErrors = true;
                showError(
                  "The WCinKgs should be an Integer greater than 0 for Filler Metal: " +
                  item.WI_FM_Size
                );
                return item;
              }


              if (numberMax6Digits(WCinKgs).status === true) {
                hasValidationErrors = true;
                showError(
                  "The WCinKgs should be an Integer with max 6 digits " +
                  item.WI_FM_Size
                );
                return item;
              }

              /**
               * NET WEIGHT CALCULATION (Kgs unit):
               * Formula: WI_WMinKgs = WI_WCinKgs ÷ CorrectionFactor
               * Purpose: Account for welding efficiency and waste
               */
              const updatedItem = {
                ...item,
                WI_WMinKgs: (WCinKgs / CorrectionFactor).toFixed(2),
              };

              return updatedItem;
            }
            if (WCinKgsNos === "Nos") {
              if (
                isNaN(WCinNos) ||
                WCinNos < 0 ||
                WCinNos === undefined
              ) {
                hasValidationErrors = true;
                showError(
                  "The WC in Nos should be an Integer : " + item.WI_FM_Size
                );
                return item;
              }


              /**
               * NET WEIGHT CALCULATION (Nos unit):
               * WI_WCinKgs already calculated: WI_WCinNos × WI_WCinKgElectrode
               * Formula: WI_WMinKgs = WI_WCinKgs ÷ CorrectionFactor
               */
              const updatedItem = {
                ...item,
                WI_WMinKgs: (WCinKgs / CorrectionFactor).toFixed(2),

              };
              return updatedItem;
            }
          }
        });


        if (!hasValidationErrors) {
          setFilteredResult(updatedResult);
          setShowConfirmModal(true);
        }
      } catch (error) {
        console.error("Failed to update data:", error);
        showError("Failed to update data");
      }
    }
  };

  const handleCloseSuccess = () => setShowSuccess(false);

  const handleConfirmAction = async () => {
    try {
      await addNewProductivity(filteredResult);
      showSuccessToast("Data updated successfully");
    } catch (error) {
      console.error("Failed to update data:", error);
      showError("Failed to update data");
    }
    setShowConfirmModal(false);
  };

  const handleCancelAction = () => {
    setShowConfirmModal(false);
  };

  useEffect(() => {
    if (reload) {
      setReload(false);
    }
  }, [reload]);

  return (
    <>
      <Container fluid className="Contain">
        <SuccessAlertModal
          msg={msg}
          show={showSuccess}
          handleClose={handleCloseSuccess}
        />
        <ConfirmAlertModal
          action={"Add"}
          handleYes={handleConfirmAction}
          handleNo={handleCancelAction}
          show={showConfirmModal}
          handleClose={() => setShowConfirmModal(false)}
          formName={"Productivity Measurement"}
        />

        <Row className="RowHeight mb-4 ml-2">
          <Col md={12} sm={12} xs={12} className="ListTitle">
            Productivity Measurement Filters
          </Col>
        </Row>


        {/* <Row className="gx-4"> */}
        <Row>
          <Col md={3}>
            <Form.Group className="ListW-input-select">
              <div className="search-select">
                <SearchSelect
                  id="WI_Slip"
                  name="WI_Slip"
                  stylegreen={error.wld_code.status ? errorstyle : normalstyle}
                  data={ProjectDetails}

                  valueField={"WI_Slip"}
                  value={WiObj.WI_Slip}
                  labelField={"WI_Slip"}
                  placeholder=""
                  onChange={async (SlipList) => {

                    setWiObj((prev) => ({
                      ...prev,
                      WI_Slip: SlipList.value,
                      WI_Welder_Id: "",
                      WI_Project_No: "",
                      WI_WPS_No: "",
                      WI_Joint_No: "",
                      WI_JointDesign: "",
                      WI_Process: "",
                      WI_CreatedBy: "",
                      WI_Welding_Position: "",
                      WI_FM_AWS: "",
                      WI_FM_Brand: "",
                      WI_Flux: "",
                      WI_Flux_Brand: "",
                      WI_SlipDate: "",
                      WI_reprintDate: "",
                      WI_plant: "",
                    }));
                    fetchSlipDetails(SlipList.value);
                  }}
                ></SearchSelect>
              </div>
              <Form.Label>Slip No</Form.Label>
            </Form.Group>
          </Col>
          <Col md={3}>
            <Form.Group>
              <div className="T-input w-100">
                <input
                  type="text"
                  id="WI_SlipDate"
                  name="WI_SlipDate"
                  disabled
                  placeholder=" "
                  className={
                    error.wld_code.status ? "err-input prodheight" : "prodheight"
                  }
                  value={wiDetails[0]?.WI_SlipDate}
                />
                <Form.Label htmlFor="wld_code">Creation Date</Form.Label>
              </div>
            </Form.Group>
          </Col>

          <Col md={3}>
            <Form.Group>
              <div className="T-input w-100">
                <input
                  type="text"
                  id="WI_reprintDate"
                  name="WI_reprintDate"
                  disabled
                  placeholder=" "
                  className={
                    error.wld_code.status ? "err-input prodheight" : "prodheight"
                  }
                  value={wiDetails[0]?.WI_reprintDate}
                />
                <Form.Label htmlFor="wld_code">Reprinted On</Form.Label>
              </div>
            </Form.Group>
          </Col>

          {displayMsg && (
            <Col
              md={12}
              sm={12}
              xs={12}
              className="ListTitle"
              style={{
                fontSize: "small",
                position: "relative",
                bottom: "17px",
                color: "var(--godrej-red-alt)",
              }}
            >
              Data already saved
            </Col>
          )}
        </Row>

        <Row style={{ position: "relative", bottom: "20px" }}>


          <Col md={3}>
            <Form.Group>
              <div className="T-input w-100">
                <input
                  type="text"
                  id="WI_Welder_Id"
                  name="WI_Welder_Id"
                  disabled
                  placeholder=" "
                  className={
                    error.wld_code.status ? "err-input prodheight" : "prodheight"
                  }
                  value={wiDetails[0]?.WI_Welder_Id}
                />
                <Form.Label htmlFor="wld_code">Welder Code</Form.Label>
              </div>
            </Form.Group>
          </Col>

          <Col md={3}>
            <Form.Group>
              <div className="T-input w-100">
                <input
                  type="text"
                  id="WI_Project_No"
                  name="WI_Project_No"
                  disabled
                  placeholder=" "
                  maxLength={10}
                  className={
                    error.wld_code.status ? "err-input prodheight" : "prodheight"
                  }
                  value={wiDetails[0]?.WI_Project_No}
                />
                <Form.Label htmlFor="wld_code">Project No.</Form.Label>
              </div>
            </Form.Group>
          </Col>

          <Col md={3}>
            <Form.Group>
              <div className="T-input w-100">
                <input
                  type="text"
                  id="WI_WPS_No"
                  name="WI_WPS_No"
                  placeholder=" "
                  disabled
                  maxLength={10}
                  className={
                    error.wld_code.status ? "err-input prodheight" : "prodheight"
                  }
                  value={wiDetails[0]?.WI_WPS_No}
                />
                <Form.Label htmlFor="wld_code">WPS No.</Form.Label>
              </div>
            </Form.Group>
          </Col>
        </Row>

        <Row style={{ position: "relative", bottom: "17px" }}>
          <Col md={3}>
            <Form.Group>
              <div className="T-input w-100">
                <input
                  type="text"
                  id="WI_JointDesign"
                  name="WI_JointDesign"
                  placeholder=" "
                  disabled
                  maxLength={10}
                  className={
                    error.wld_code.status ? "err-input prodheight" : "prodheight"
                  }
                  value={wiDetails[0]?.WI_JointDesign}
                />
                <Form.Label htmlFor="wld_code">Joint Design</Form.Label>
              </div>
              <div className="required-text">
                {error.wld_code.status && (
                  <Form.Text className="text-danger">
                    {error.wld_code.message}
                  </Form.Text>
                )}
              </div>
            </Form.Group>
          </Col>
          <Col md={3}>
            <Form.Group>
              <div className="T-input w-100">
                <input
                  type="text"
                  id="WI_Process"
                  name="WI_Process"
                  placeholder=" "
                  disabled
                  maxLength={10}
                  className={
                    error.wld_code.status ? "err-input prodheight" : "prodheight"
                  }
                  value={wiDetails[0]?.WI_Process}
                />
                <Form.Label htmlFor="wld_code">Process</Form.Label>
              </div>
              <div className="required-text">
                {error.wld_code.status && (
                  <Form.Text className="text-danger">
                    {error.wld_code.message}
                  </Form.Text>
                )}
              </div>
            </Form.Group>
          </Col>
          <Col md={3}>
            <Form.Group>
              <div className="T-input w-100">
                <input
                  type="text"
                  id="WI_CreatedBy"
                  name="WI_CreatedBy"
                  placeholder=" "
                  disabled
                  maxLength={10}
                  className={
                    error.wld_code.status ? "err-input prodheight" : "prodheight"
                  }
                  value={wiDetails[0]?.WI_CreatedBy}
                />
                <Form.Label htmlFor="wld_code">Created By</Form.Label>
              </div>
              <div className="required-text">
                {error.wld_code.status && (
                  <Form.Text className="text-danger">
                    {error.wld_code.message}
                  </Form.Text>
                )}
              </div>
            </Form.Group>
          </Col>
          <Col md={3}>
            <Form.Group>
              <div className="T-input w-100">
                <input
                  type="text"
                  id="WI_Welding_Position"
                  name="WI_Welding_Position"
                  placeholder=" "
                  disabled
                  maxLength={10}
                  className={
                    error.wld_code.status ? "err-input prodheight" : "prodheight"
                  }
                  value={wiDetails[0]?.WI_Welding_Position}
                />
                <Form.Label htmlFor="wld_code">Welding Position</Form.Label>
              </div>
              <div className="required-text">
                {error.wld_code.status && (
                  <Form.Text className="text-danger">
                    {error.wld_code.message}
                  </Form.Text>
                )}
              </div>
            </Form.Group>
          </Col>
        </Row>

        <Row style={{ position: "relative", bottom: "14px" }}>
          <Col md={3}>
            <Form.Group>
              <div className="T-input w-100">
                <input
                  type="text"
                  id="WI_FM_AWS"
                  name="WI_FM_AWS"
                  placeholder=" "
                  disabled
                  maxLength={10}
                  className={
                    error.wld_code.status ? "err-input prodheight" : "prodheight"
                  }
                  value={wiDetails[0]?.WI_FM_AWS}
                />
                <Form.Label htmlFor="wld_code">Filler Metal(AWS)</Form.Label>
              </div>
              <div className="required-text">
                {error.wld_code.status && (
                  <Form.Text className="text-danger">
                    {error.wld_code.message}
                  </Form.Text>
                )}
              </div>
            </Form.Group>
          </Col>
          <Col md={3}>
            <Form.Group>
              <div className="T-input w-100">
                <input
                  type="text"
                  id="WI_FM_Brand"
                  name="WI_FM_Brand"
                  placeholder=" "
                  disabled
                  maxLength={10}
                  className={
                    error.wld_code.status ? "err-input prodheight" : "prodheight"
                  }
                  value={wiDetails[0]?.WI_FM_Brand}
                />
                <Form.Label htmlFor="wld_code">Filler Metal Brand</Form.Label>
              </div>
              <div className="required-text">
                {error.wld_code.status && (
                  <Form.Text className="text-danger">
                    {error.wld_code.message}
                  </Form.Text>
                )}
              </div>
            </Form.Group>
          </Col>
          <Col md={3}>
            <Form.Group>
              <div className="T-input w-100">
                <input
                  type="text"
                  id="WI_Flux"
                  name="WI_Flux"
                  placeholder=" "
                  disabled
                  maxLength={10}
                  className={
                    error.wld_code.status ? "err-input prodheight" : "prodheight"
                  }
                  value={wiDetails[0]?.WI_Flux}
                />
                <Form.Label htmlFor="wld_code">Flux</Form.Label>
              </div>
              <div className="required-text">
                {error.wld_code.status && (
                  <Form.Text className="text-danger">
                    {error.wld_code.message}
                  </Form.Text>
                )}
              </div>
            </Form.Group>
          </Col>
          <Col md={3}>
            <Form.Group>
              <div className="T-input w-100">
                <input
                  type="text"
                  id="WI_Flux_Brand"
                  name="WI_Flux_Brand"
                  placeholder=" "
                  disabled
                  maxLength={10}
                  className={
                    error.wld_code.status ? "err-input prodheight" : "prodheight"
                  }
                  value={wiDetails[0]?.WI_Flux_Brand}
                />
                <Form.Label htmlFor="wld_code">Flux Brand</Form.Label>
              </div>
              <div className="required-text">
                {error.wld_code.status && (
                  <Form.Text className="text-danger">
                    {error.wld_code.message}
                  </Form.Text>
                )}
              </div>
            </Form.Group>
          </Col>
        </Row>

        <Row>
          <Col
            md={12}
            sm={12}
            xs={12}
            style={{
              fontSize: "small",
              color: "var(--godrej-red-alt)",
              position: "relative",
              bottom: "20px",
            }}
          >
            Each item to be saved row by row : Flux item is displayed with Filler
            Metal Size 99999
          </Col>
        </Row>
        <GridMaster
          rowDetails={filteredResult}
          colDetails={columnDefs}
          defaultColDef={{ resizable: true }}
          fieldNames={fieldName}
          headerNames={headerName}
          setExcelData={setExcelData}
          ref={childRef}
          gridOptions={gridOptions}
          onCellValueChanged={CellEditing}
          pagination={false}
          height={250}
        />
        <Toaster />
      </Container>
      <Row>
        <Col>
          <div>
            {!displayMsg && (
              <Button onClick={handlesubmition} className="prodsave">
                Save
              </Button>
            )}
          </div>
        </Col>
      </Row>
    </>
  );
});

export default PRODLandPage;